/**
 * Backend-backed auth replacing AWS Amplify/Cognito. Produces a Cognito-shaped
 * "session user" object so the rest of the legacy app (useUser, permissions,
 * hasPermission, navbar avatar, ...) keeps working unchanged. The token comes
 * from aristotle-backend's `POST /auth/login` and is stored via auth/session.
 *
 * Backend contract (/v1 prefix, set in PublicGlobals.API_BASE_URL):
 *   POST /auth/login  { email, password }  + header X-Org-Slug  -> { token }
 *   POST /auth/verify { token }                                 -> { userID, orgID }
 *   GET  /user/me                                               -> { id, firstName, lastName, type, email, status, ... }
 * Roles: USER | ADMIN | SUPER_ADMIN. Legacy permission groups: admin/owner = all
 * permissions, user = none.
 */
import { APIClient } from '../APIClient'

import {
  clearDeviceToken,
  clearToken,
  getDeviceToken,
  getOrgSlug,
  getToken,
  setDeviceToken,
  setToken,
} from './session'

export type BackendUserType = 'USER' | 'ADMIN' | 'SUPER_ADMIN'

export interface BackendMe {
  id: number
  firstName: string
  lastName: string
  type: BackendUserType
  email: string
  status?: string
  canRecordVideos?: boolean
  profilePictureId?: number
}

/** Map the backend role to the legacy Cognito group names used by permissions.ts. */
export const mapTypeToGroups = (type?: BackendUserType): string[] => {
  switch (type) {
    case 'SUPER_ADMIN':
    case 'ADMIN':
      return ['admin']
    default:
      return ['user']
  }
}

/**
 * Build a Cognito-`CognitoUserInterface`-shaped object the legacy code reads
 * from (signInUserSession.idToken.payload['cognito:groups'], attributes, etc.).
 */
export const buildSessionUser = (token: string, me: BackendMe, orgID?: number | null) => {
  const groups = mapTypeToGroups(me?.type)
  const payload = {
    'cognito:groups': groups,
    email: me?.email,
    sub: String(me?.id),
    'custom:orgId': orgID != null ? String(orgID) : undefined,
  }
  return {
    username: me?.email,
    attributes: {
      email: me?.email,
      given_name: me?.firstName,
      family_name: me?.lastName,
      phone_number_verified: false,
    },
    signInUserSession: {
      idToken: { payload, jwtToken: token },
      accessToken: { payload, jwtToken: token },
    },
    // Surface the raw backend user for convenience.
    _backendUser: me,
    _orgID: orgID,
  }
}

/** Shape of POST /auth/login and POST /auth/login/2fa responses. */
interface LoginApiResponse {
  token?: string
  requires2FA?: boolean
  tempToken?: string
  hasSeenTwoFactorSetup?: boolean
  deviceToken?: string
}

/**
 * Result of a login attempt. Either the session is established (`status:
 * 'authenticated'`, with the resolved session user) or the account has 2FA and
 * the caller must complete the challenge with `backendVerify2FA(tempToken, …)`.
 */
export interface AuthenticatedResult {
  status: 'authenticated'
  user: any
  hasSeenTwoFactorSetup?: boolean
}

export type LoginResult =
  | AuthenticatedResult
  | { status: 'requires2FA'; tempToken: string }

/**
 * POST /auth/login. Forwards any trusted-device token so a 2FA account can skip
 * the challenge on this device. Returns either an established session or a 2FA
 * challenge handle.
 */
export const backendLogin = async (
  email: string,
  password: string
): Promise<LoginResult> => {
  const orgSlug = getOrgSlug()
  const deviceToken = getDeviceToken() ?? undefined
  const res = await APIClient.makeAPICall<LoginApiResponse>('POST', '/auth/login', {
    body: { email, password, ...(deviceToken && { deviceToken }) },
    noAuthorizationHeader: true,
    ...(orgSlug && { headers: { 'X-Org-Slug': orgSlug } }),
  })

  if (res.requires2FA && res.tempToken) {
    return { status: 'requires2FA', tempToken: res.tempToken }
  }
  if (!res.token) throw new Error('Login failed: no token returned')
  setToken(res.token)
  const user = await getSessionUser()
  return {
    status: 'authenticated',
    user,
    hasSeenTwoFactorSetup: res.hasSeenTwoFactorSetup,
  }
}

/**
 * POST /auth/login/2fa. Completes a 2FA challenge with the 6-digit code; on
 * success stores the session token (and the 30-day device token when the user
 * chose to trust this device) and resolves the session user.
 */
export const backendVerify2FA = async (
  tempToken: string,
  code: string,
  trustDevice: boolean
): Promise<AuthenticatedResult> => {
  const res = await APIClient.makeAPICall<LoginApiResponse>('POST', '/auth/login/2fa', {
    body: { tempToken, code, trustDevice },
    noAuthorizationHeader: true,
  })
  if (!res.token) throw new Error('Verification failed: no token returned')
  setToken(res.token)
  if (res.deviceToken) setDeviceToken(res.deviceToken)
  const user = await getSessionUser()
  return {
    status: 'authenticated',
    user,
    hasSeenTwoFactorSetup: res.hasSeenTwoFactorSetup,
  }
}

/** Resolve the current session user from the stored token, or null if none/invalid. */
export const getSessionUser = async () => {
  const token = getToken()
  if (!token) return null
  // verify gives us the orgID; /user/me gives the profile + role.
  const identity = await APIClient.makeAPICall<{ userID: number; orgID: number | null }>(
    'POST',
    '/auth/verify',
    { body: { token }, noAuthorizationHeader: true }
  ).catch(() => null)
  const me = await APIClient.makeAPICall<BackendMe>('GET', '/user/me')
  return buildSessionUser(token, me, identity?.orgID)
}

export const backendSignOut = (): void => {
  clearToken()
}
